import { useEffect, useState } from "react";
import InfoItem from "./InfoItem";
import styles from "./styles.module.css";
import { LuInfo } from "react-icons/lu";
import { removeURLQueryParams } from "../../../../utils/utils";
import PropTypes from "prop-types";
import { GrCircleQuestion } from "react-icons/gr";
import axiosInstance from "../../../../api/axios";

const GeneralInfo = ({ infoData, loader, digitalIdentity }) => {
  const [signatureImageUrl, setSignatureImageUrl] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  useEffect(() => {
    const fetchSignatureImage = async () => {
      if (!infoData?.signature_link) {
        setSignatureImageUrl("");
        return;
      }
      if (!digitalIdentity) return;
      const controller = digitalIdentity?.controller_code || "";
      const scope = digitalIdentity?.customer_scope || "";
      const type = digitalIdentity?.type || "";
      const value = digitalIdentity?.value || "";
      const purpose = infoData?.global_purpose || "";
      const url = infoData?.sk
        ? `/signature/${controller}/${scope}/${type}/${value}/${purpose}/history/${encodeURIComponent(infoData.sk)}`
        : `/signature/${controller}/${scope}/${type}/${value}/${purpose}`;

      try {
        setImageLoading(true);
        const resp = await axiosInstance.get(url);
        const imageUrl =
          resp?.data?.data?.image_url || resp?.data?.image_url || "";
        setSignatureImageUrl(imageUrl);
        setImageLoading(false);
      } catch (err) {
        console.error("Failed to fetch signature image:", err);
        setSignatureImageUrl("");
        setImageLoading(false);
      }
    };

    fetchSignatureImage();
  }, [
    infoData?.signature_link,
    infoData?.sk,
    infoData?.global_purpose,
    digitalIdentity,
  ]);
  return (
    <div className={styles["gen-info-container"]}>
      <InfoItem label="Purpose" value={infoData?.global_purpose} />
      <InfoItem label="Legal basis" value={infoData?.initial_legal_basis} />
      <InfoItem label="Current Status" value={infoData?.status} />
      <InfoItem label="Status Reason" value={infoData?.status_reason} />
      <InfoItem
        label="Last Update Channel"
        value={infoData?.last_update_channel}
      />
      <InfoItem
        label="Valid From"
        value={infoData?.valid_from || infoData?.created_datetime}
        valueType="dateTime_hhmm"
        toolTipLabel={infoData?.valid_from || infoData?.created_datetime}
        toolTipIcon={<LuInfo className="table__date__icon" size={19}/>}
        toolTipPosition="right"
      />
      <InfoItem
        label="Capture Date"
        value={infoData?.capture_date}
        valueType="original-date"
        toolTipLabel={"DD.MM.YYYY"}
        titleMoreInfo="Value based on the Time Zone setting in the Configurations"
        titleMoreIcon={<GrCircleQuestion className="table__date__icon m-0" size={19}/>}
      />
      <InfoItem
        label="Capture Time"
        value={infoData?.capture_time}
        valueType="original-time"
        titleMoreInfo="Value based on the Time Zone setting in the Configurations"
        titleMoreIcon={<GrCircleQuestion className="table__date__icon m-0" size={19}/>}
      />
      <InfoItem label="Capture Source" value={infoData?.capture_source} />
      <InfoItem
        label="Expiry Date"
        value={infoData?.expiry_date}
        valueType="date"
        toolTipLabel={"DD.MM.YYYY"}
      />
      <InfoItem
        label="Collection Channel"
        value={infoData?.collection_channel}
      />
      <InfoItem
        label="Collector Email"
        //itemStyle={styles["three-column"]}
        value={infoData?.collector_email}
      />
      <InfoItem
        label="Valid To"
        value={infoData?.valid_to || infoData?.last_modified_datetime}
        valueType="dateTime_hhmm"
        toolTipLabel={infoData?.valid_to || infoData?.last_modified_datetime}
        toolTipIcon={<LuInfo className="table__date__icon" size={19}/>}
        toolTipPosition="right"
      />

      <InfoItem
        label="Last Modified By"
        value={infoData?.lastModifiedBy?.displayName}
        loader={loader}
      />

      <InfoItem
        label="Last Status Change"
        value={infoData?.last_status_change_datetime}
        valueType="dateTime_hhmm"
        toolTipLabel={infoData?.last_status_change_datetime}
        toolTipIcon={<LuInfo className="table__date__icon" size={19} />}
        toolTipPosition="right"
      />
      <InfoItem
        label="Signature"
        {...(infoData?.signature_link && {
          value: signatureImageUrl || infoData?.signature_link || "N/A",
          valueType: "link",
          valuePlaceholder: "View",
        })}
        loader={imageLoading}
      />
      <InfoItem
        label="Signature Paper ID"
        value={infoData?.signature_paper_id}
      />
      <InfoItem
        label="Statement URL"
        value={removeURLQueryParams(infoData?.statement_url)}
        valueType="link"
      />
      <InfoItem
        label="Document Version"
        value={infoData?.document_version_number}
      />
      <InfoItem
        label="Commit Link"
        value={
          infoData?.commit_hash
            ? `https://github.com/bayer-int/consent-management-ng-documents/tree/${infoData?.commit_hash}`
            : ""
        }
        valueType="link"
      />
      <InfoItem
        label="Unsub Trigger Brand ID"
        value={infoData?.unsubscribe_trigger_brand_id}
      />
      <InfoItem
        label="Unsub Trigger Content ID"
        value={infoData?.unsubscribe_trigger_content_id}
      />
    </div>
  );
};

GeneralInfo.propTypes = {
  infoData: PropTypes.shape({
    global_purpose: PropTypes.string,
    initial_legal_basis: PropTypes.string,
    status: PropTypes.string,
    status_reason: PropTypes.string,
    last_update_channel: PropTypes.string,
    valid_from: PropTypes.string,
    created_datetime: PropTypes.string,
    capture_date: PropTypes.string,
    capture_time: PropTypes.string,
    capture_source: PropTypes.string,
    expiry_date: PropTypes.string,
    collection_channel: PropTypes.string,
    collector_email: PropTypes.string,
    valid_to: PropTypes.string,
    last_modified_datetime: PropTypes.string,
    lastModifiedBy: PropTypes.shape({
      displayName: PropTypes.string,
    }),
    last_status_change_datetime: PropTypes.string,
    sk: PropTypes.string,
    signature_link: PropTypes.string,
    signature_paper_id: PropTypes.string,
    statement_url: PropTypes.string,
    document_version_number: PropTypes.string,
    commit_hash: PropTypes.string,
    unsubscribe_trigger_brand_id: PropTypes.string,
    unsubscribe_trigger_content_id: PropTypes.string,
  }),
  loader: PropTypes.bool,
  digitalIdentity: PropTypes.shape({
    controller_code: PropTypes.string,
    customer_scope: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
};

export default GeneralInfo;
